import api, { API_ENDPOINTS } from '../config/api';

// Proper Leads Service for managing leads using dedicated leads endpoint
// Connected to MongoDB database via API
export class LeadsService {
  
  // Create a new lead
  static async createLead(leadData) {
    try {
      console.log('🔄 Creating lead with data:', leadData);
      console.log('🔄 Custom fields from form:', leadData.customFields);
      
      // Use customFields directly from leadData if it exists
      const customFields = leadData.customFields || {};

      // Simplified payload for local backend
      const leadPayload = {
        title: leadData.title || 'New Lead',
        name: leadData.name,
        email: leadData.email,
        phone: leadData.phone,
        company: leadData.company,
        source: leadData.source || 'website',
        budget: leadData.budget,
        status: leadData.status || 'new',
        priority: leadData.priority || 'medium',
        notes: leadData.notes || '',
        value: leadData.value || 0,
        customFields: customFields, // Use custom fields from form
        // Include assignment data
        assignedTo: leadData.assignedTo || [],
        assignedEmployees: leadData.assignedEmployees || [],
        createdAt: new Date().toISOString(),
        lastContact: new Date().toISOString()
      };

      console.log('📤 Sending lead payload:', leadPayload);
      console.log('📤 Custom fields in payload:', leadPayload.customFields);
      console.log('📤 Assignment data:', { 
        assignedTo: leadPayload.assignedTo, 
        assignedEmployees: leadPayload.assignedEmployees 
      });
      const response = await api.post(API_ENDPOINTS.LEADS, leadPayload);
      console.log('✅ Lead created successfully:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error creating lead:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return { success: false, error: error.response?.data?.message || 'Failed to create lead. Please ensure the leads API is deployed.' };
    }
  }

  // Get all leads
  static async getAllLeads() {
    try {
      console.log('🔍 Fetching leads from:', API_ENDPOINTS.LEADS);
      console.log('🔍 Full API URL:', api.defaults.baseURL + API_ENDPOINTS.LEADS);
      
      // Check if we have auth token
      const token = localStorage.getItem('authToken');
      console.log('🔑 Auth token available:', !!token);
      if (token) {
        console.log('🔑 Token preview:', token.substring(0, 20) + '...');
      }
      
      // Request all leads without pagination limit
      const response = await api.get(API_ENDPOINTS.LEADS);
      console.log('📊 Leads API response status:', response.status);
      console.log('📊 Leads API response:', response);
      
      // Extract leads from backend response
      const responseData = response.data;
      console.log('📋 Response data structure:', {
        hasLeads: 'leads' in responseData,
        hasData: 'data' in responseData,
        isArray: Array.isArray(responseData),
        keys: Object.keys(responseData),
        fullData: responseData
      });
      
      let leads = [];
      
      // Handle different response formats - backend returns { leads: [...] }
      if (responseData.leads && Array.isArray(responseData.leads)) {
        leads = responseData.leads;
        console.log('✅ Using leads array from response');
      } else if (responseData.data && Array.isArray(responseData.data)) {
        leads = responseData.data;
        console.log('✅ Using data array from response');
      } else if (Array.isArray(responseData)) {
        leads = responseData;
        console.log('✅ Using direct array response');
      } else {
        console.warn('⚠️ Unexpected response format, returning empty array');
        console.warn('⚠️ Response data:', responseData);
        leads = [];
      }
      
      console.log(`📊 Found ${leads.length} leads`);
      return { success: true, data: leads };
    } catch (error) {
      console.error('❌ Error fetching leads:', error);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error message:', error.response?.data?.message);
      console.error('❌ Full error response:', error.response?.data);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.config?.headers,
        url: error.config?.url
      });
      
      return { 
        success: false, 
        error: error.response?.data?.message || 
               `Failed to fetch leads: ${error.message}. Status: ${error.response?.status}` 
      };
    }
  }

  // Get a single lead by ID
  static async getLeadById(leadId) {
    console.log('LeadsService: Fetching lead by ID:', leadId);
    console.log('LeadsService: API endpoint:', API_ENDPOINTS.LEAD_BY_ID(leadId));
    try {
      const response = await api.get(API_ENDPOINTS.LEAD_BY_ID(leadId));
      console.log('LeadsService: Lead API response:', response);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('LeadsService: Error fetching lead:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch lead' };
    }
  }

  // Update a lead
  static async updateLead(leadId, leadData) {
    const leadPayload = {
      title: leadData.title,
      name: leadData.name,
      email: leadData.email,
      phone: leadData.phone,
      company: leadData.company,
      source: leadData.source,
      budget: leadData.budget,
      status: leadData.status,
      statusRemark: leadData.statusRemark || '',
      priority: leadData.priority || 'medium',
      value: leadData.value || 0,
      notes: leadData.notes || '',
      customFields: leadData.customFields || {}, // Include custom fields
      lastContact: new Date().toISOString()
    };

    try {
      console.log('🔄 Updating lead:', leadId, 'with status:', leadData.status, 'remark:', leadData.statusRemark);
      console.log('🔄 Custom fields in update:', leadPayload.customFields);
      const response = await api.put(API_ENDPOINTS.LEAD_BY_ID(leadId), leadPayload);
      console.log('✅ Lead updated successfully:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error updating lead:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return { success: false, error: error.response?.data?.message || 'Failed to update lead. Please ensure the leads API is deployed.' };
    }
  }

  // Delete a lead
  static async deleteLead(leadId) {
    try {
      await api.delete(API_ENDPOINTS.LEAD_BY_ID(leadId));
      
      // Import notification service and clean up notifications for this lead
      const notificationService = (await import('./notificationService')).default;
      notificationService.deleteNotificationsForLead(leadId);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting lead:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to delete lead. Please ensure the leads API is deployed.' };
    }
  }

  // Update lead status
  static async updateLeadStatus(leadId, status) {
    try {
      console.log('LeadsService: Updating lead status:', { leadId, status });
      console.log('LeadsService: API endpoint:', API_ENDPOINTS.LEAD_BY_ID(leadId));
      
      const payload = { 
        status: status,
        lastContact: new Date().toISOString()
      };
      
      console.log('LeadsService: Payload:', payload);
      
      const response = await api.put(API_ENDPOINTS.LEAD_BY_ID(leadId), payload);
      console.log('LeadsService: Status update response:', response.data);
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('LeadsService: Error updating lead status:', error);
      console.error('LeadsService: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return { 
        success: false, 
        error: error.response?.data?.message || `Failed to update lead status: ${error.message}` 
      };
    }
  }

  // Search leads
  static async searchLeads(searchTerm) {
    try {
      const response = await api.get(`${API_ENDPOINTS.LEADS}?search=${encodeURIComponent(searchTerm)}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error searching leads:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to search leads' };
    }
  }

  // Get leads by status
  static async getLeadsByStatus(status) {
    try {
      const response = await api.get(`${API_ENDPOINTS.LEADS}?status=${status}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching leads by status:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch leads by status' };
    }
  }
}

export default LeadsService;
